import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Loader2, Save, Trash2, Box, ExternalLink, Sparkles,
  Archive, ArchiveRestore, Plus, FileSearch, Swords, Users, Target,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ChipListEditor } from "@/components/ChipListEditor";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { SectionAutofillButton } from "@/components/SectionAutofillButton";

const WRITE_ROLES = ["owner", "admin", "researcher"];
const RELATION_TYPES = ["direct", "indirect", "aspirational"];

export default function ProductDetail() {
  const { t } = useTranslation(["products", "common", "errors", "autofill", "competitors", "brands"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);

  // Competitor sub-modals
  const [openCompetitorCreate, setOpenCompetitorCreate] = useState(false);
  const [openCompetitorSuggest, setOpenCompetitorSuggest] = useState(false);
  const [pendingCompetitorDelete, setPendingCompetitorDelete] = useState(null);
  const [competitorDeleting, setCompetitorDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get(`/products/${id}`);
      setProduct(p);
      const [bRes, cRes] = await Promise.all([
        api.get(`/brands/${p.brand_id}`),
        api.get(`/products/${id}/competitors?include_brand=true`),
      ]);
      setBrand(bRes.data);
      setCompetitors(cRes.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate("/products");
        return;
      }
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const isArchived = !!product?.archived_at;

  const onSave = async (e) => {
    e.preventDefault();
    if (!product) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/products/${product.id}`, {
        name: product.name?.trim(),
        url: product.url || null,
        category: product.category || null,
        features: product.features || [],
        benefits: product.benefits || [],
        pricing: product.pricing || null,
        icp: product.icp || null,
        // Phase 7 strategic
        tagline: product.tagline || null,
        product_promise: product.product_promise || null,
        usps: product.usps || [],
        hero_benefit: product.hero_benefit || null,
        jobs_to_be_done: product.jobs_to_be_done || [],
        objections: product.objections || [],
        proof_points: product.proof_points || [],
        // Phase 7 audience deep-dive
        audience_pains: product.audience_pains || [],
        audience_desires: product.audience_desires || [],
        audience_previous_attempts: product.audience_previous_attempts || [],
        audience_deep_desires: product.audience_deep_desires || [],
        audience_expressions: product.audience_expressions || [],
        audience_demographics: product.audience_demographics || null,
        audience_psychographics: product.audience_psychographics || [],
        audience_firmographics: product.audience_firmographics || null,
      });
      setProduct(data);
      toast.success(t("products:detail.saveSuccess"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setSaving(false); }
  };

  const onArchive = async () => {
    if (!product) return;
    setArchiveBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/archive`);
      setProduct({ ...product, archived_at: data.archived_at });
      toast.success(t("products:detail.archived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setArchiveBusy(false); }
  };

  const onUnarchive = async () => {
    if (!product) return;
    setArchiveBusy(true);
    try {
      await api.post(`/products/${product.id}/unarchive`);
      setProduct({ ...product, archived_at: null });
      toast.success(t("products:detail.unarchived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setArchiveBusy(false); }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/products/${product.id}`);
      toast.success(t("products:detail.deleted"));
      navigate(`/brands/${product.brand_id}`);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setDeleting(false); setConfirmDelete(false); }
  };

  const confirmDeleteCompetitor = async () => {
    if (!pendingCompetitorDelete) return;
    setCompetitorDeleting(true);
    try {
      await api.delete(`/competitors/${pendingCompetitorDelete.id}`);
      setCompetitors((prev) => prev.filter((c) => c.id !== pendingCompetitorDelete.id));
      toast.success(t("competitors:deleted"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setCompetitorDeleting(false); setPendingCompetitorDelete(null); }
  };

  // Phase 9 — section-scoped Fill with AI. Drop null/empty values so the LLM
  // can't blank a user's manual entry by emitting `null` for fields outside
  // the focused section.
  const productHasCachedSources = !!(product?.autofill_source?.raw_pages?.length);
  const mergeProductDraft = (draft) => {
    if (!draft || typeof draft !== "object" || !product) return;
    const patch = {};
    for (const [k, v] of Object.entries(draft)) {
      if (v === null || v === undefined) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      patch[k] = v;
    }
    setProduct((prev) => ({ ...(prev || product), ...patch }));
  };
  const productAiFill = (sectionKey, sectionLabel) =>
    canWrite && product ? (
      <SectionAutofillButton
        entityType="product"
        entityId={product.id}
        sectionKey={sectionKey}
        sectionLabel={sectionLabel}
        testIdPrefix={`product-section-${sectionKey}`}
        hasCachedSources={productHasCachedSources}
        onDraft={mergeProductDraft}
      />
    ) : null;

  if (loading || !product) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  return (
    <div className={`space-y-8 ${isArchived ? "opacity-90" : ""}`} data-testid="product-detail-page">
      {isArchived && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="product-archive-banner">
          <div className="flex items-start gap-2">
            <Archive className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={1.75} />
            <div>
              <p className="font-display text-sm font-semibold text-amber-900">{t("products:detail.archivedTitle")}</p>
              <p className="text-[11px] text-amber-800/80">{t("products:detail.archivedHint")}</p>
            </div>
          </div>
          {canWrite && (
            <Button
              type="button" size="sm" variant="outline"
              onClick={onUnarchive} disabled={archiveBusy}
              data-testid="product-unarchive-button"
              className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            >
              {archiveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ArchiveRestore className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} /> {t("products:detail.unarchiveAction")}</>}
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Link to="/products" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900" data-testid="product-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("products:detail.backToList")}
        </Link>
        {product.autofill_source && (
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-700">
            <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("autofill:aiBadge")}
          </Badge>
        )}
      </div>

      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <Box className="mr-1 inline h-3 w-3" strokeWidth={2} /> {brand?.name}
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{product.name}</h1>
        {product.tagline && <p className="max-w-2xl text-sm text-slate-600">{product.tagline}</p>}
        {product.url && (
          <a href={product.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600">
            {product.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
        )}
        {brand && (
          <Link to={`/brands/${brand.id}`} className="mt-1 inline-block text-xs font-medium text-indigo-600 hover:text-indigo-900" data-testid="product-open-brand">
            {t("products:detail.openBrand")} →
          </Link>
        )}
      </header>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="product-tabs">
          <TabsTrigger value="overview" data-testid="product-tab-overview">{t("products:detail.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="features" data-testid="product-tab-features">{t("products:detail.tabs.features")}</TabsTrigger>
          <TabsTrigger value="audience" data-testid="product-tab-audience">{t("products:detail.tabs.audience")}</TabsTrigger>
          <TabsTrigger value="competitors" data-testid="product-tab-competitors">
            {t("products:detail.tabs.competitors")} <span className="ml-1 text-[10px] text-slate-400">{competitors.length}</span>
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="product-tab-sources">{t("products:detail.tabs.sources")}</TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW ─── */}
        <TabsContent value="overview">
          <form onSubmit={onSave} className="space-y-6" noValidate>
            <CollapsibleSection
              testId="product-section-identity"
              title={t("products:sections.identity.title")}
              subtitle={t("products:sections.identity.subtitle")}
              aiFill={productAiFill("overview", t("products:sections.identity.title"))}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field label={t("products:form.nameLabel")} required>
                  <Input required disabled={!canWrite} value={product.name || ""}
                         onChange={(e) => setProduct({ ...product, name: e.target.value })}
                         data-testid="product-edit-name" className="h-11" />
                </Field>
                <Field label={t("products:form.urlLabel")}>
                  <Input type="url" disabled={!canWrite} value={product.url || ""}
                         onChange={(e) => setProduct({ ...product, url: e.target.value })}
                         data-testid="product-edit-url" className="h-11" />
                </Field>
                <Field label={t("products:form.categoryLabel")}>
                  <Input disabled={!canWrite} value={product.category || ""}
                         onChange={(e) => setProduct({ ...product, category: e.target.value })}
                         data-testid="product-edit-category" className="h-11" />
                </Field>
                <Field label={t("products:form.taglineLabel")} hint={t("products:form.taglineHint")}>
                  <Input disabled={!canWrite} value={product.tagline || ""}
                         onChange={(e) => setProduct({ ...product, tagline: e.target.value })}
                         data-testid="product-edit-tagline" className="h-11" />
                </Field>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              testId="product-section-promise"
              title={t("products:sections.promise.title")}
              subtitle={t("products:sections.promise.subtitle")}
              aiFill={productAiFill("overview", t("products:sections.promise.title"))}
            >
              <div className="space-y-4">
                <Field label={t("products:form.productPromiseLabel")} hint={t("products:form.productPromiseHint")}>
                  <Textarea disabled={!canWrite} value={product.product_promise || ""}
                            onChange={(e) => setProduct({ ...product, product_promise: e.target.value })}
                            data-testid="product-edit-product-promise" rows={4} />
                </Field>
                <Field label={t("products:form.heroBenefitLabel")} hint={t("products:form.heroBenefitHint")}>
                  <Textarea disabled={!canWrite} value={product.hero_benefit || ""}
                            onChange={(e) => setProduct({ ...product, hero_benefit: e.target.value })}
                            data-testid="product-edit-hero-benefit" rows={3} />
                </Field>
                <Field label={t("products:form.pricingLabel")}>
                  <Textarea disabled={!canWrite} value={product.pricing || ""}
                            onChange={(e) => setProduct({ ...product, pricing: e.target.value })}
                            placeholder={t("products:form.pricingPlaceholder")}
                            data-testid="product-edit-pricing" rows={3} />
                </Field>
              </div>
            </CollapsibleSection>

            {canWrite && <FormFooter saving={saving} onDelete={() => setConfirmDelete(true)} onArchive={onArchive} archiveBusy={archiveBusy} isArchived={isArchived} t={t} />}
          </form>
        </TabsContent>

        {/* ─── FEATURES & BENEFITS ─── */}
        <TabsContent value="features">
          <form onSubmit={onSave} className="space-y-6" noValidate>
            <CollapsibleSection
              testId="product-section-features"
              title={t("products:sections.features.title")}
              subtitle={t("products:sections.features.subtitle")}
              aiFill={productAiFill("features_benefits", t("products:sections.features.title"))}
            >
              <div className="space-y-4">
                <Field label={t("products:form.featuresLabel")} hint={t("products:form.featuresHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.features || []}
                    onChange={(v) => setProduct({ ...product, features: v })}
                    testId="product-edit-features"
                    placeholder={t("products:form.featuresPlaceholder")} />
                </Field>
                <Field label={t("products:form.benefitsLabel")} hint={t("products:form.benefitsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.benefits || []}
                    onChange={(v) => setProduct({ ...product, benefits: v })}
                    testId="product-edit-benefits"
                    placeholder={t("products:form.benefitsPlaceholder")} />
                </Field>
                <Field label={t("products:form.uspsLabel")} hint={t("products:form.uspsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.usps || []}
                    onChange={(v) => setProduct({ ...product, usps: v })}
                    testId="product-edit-usps"
                    placeholder={t("products:form.uspsPlaceholder")} />
                </Field>
                <Field label={t("products:form.jobsToBeDoneLabel")} hint={t("products:form.jobsToBeDoneHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.jobs_to_be_done || []}
                    onChange={(v) => setProduct({ ...product, jobs_to_be_done: v })}
                    testId="product-edit-jobs-to-be-done"
                    placeholder={t("products:form.jobsToBeDonePlaceholder")} />
                </Field>
                <Field label={t("products:form.objectionsLabel")} hint={t("products:form.objectionsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.objections || []}
                    onChange={(v) => setProduct({ ...product, objections: v })}
                    testId="product-edit-objections" chipTone="amber"
                    placeholder={t("products:form.objectionsPlaceholder")} />
                </Field>
                <Field label={t("products:form.proofPointsLabel")} hint={t("products:form.proofPointsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.proof_points || []}
                    onChange={(v) => setProduct({ ...product, proof_points: v })}
                    testId="product-edit-proof-points"
                    placeholder={t("products:form.proofPointsPlaceholder")} />
                </Field>
              </div>
            </CollapsibleSection>
            {canWrite && <FormFooter saving={saving} onDelete={() => setConfirmDelete(true)} onArchive={onArchive} archiveBusy={archiveBusy} isArchived={isArchived} t={t} />}
          </form>
        </TabsContent>

        {/* ─── AUDIENCE ─── */}
        <TabsContent value="audience">
          <form onSubmit={onSave} className="space-y-6" noValidate>
            <CollapsibleSection
              testId="product-section-icp"
              title={t("products:sections.icp.title")}
              subtitle={t("products:sections.icp.subtitle")}
              aiFill={productAiFill("audience", t("products:sections.icp.title"))}
            >
              <Field label={t("products:form.icpLabel")} hint={t("products:form.icpHint")}>
                <Textarea disabled={!canWrite} value={product.icp || ""}
                          onChange={(e) => setProduct({ ...product, icp: e.target.value })}
                          placeholder={t("products:form.icpPlaceholder")}
                          data-testid="product-edit-icp" rows={4} />
              </Field>
            </CollapsibleSection>

            <CollapsibleSection
              testId="product-section-pains-desires"
              title={t("products:sections.painsDesires.title")}
              subtitle={t("products:sections.painsDesires.subtitle")}
              aiFill={productAiFill("audience_deep_dive", t("products:sections.painsDesires.title"))}
            >
              <div className="space-y-4">
                <Field label={t("products:form.audiencePainsLabel")} hint={t("products:form.audiencePainsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.audience_pains || []}
                    onChange={(v) => setProduct({ ...product, audience_pains: v })}
                    testId="product-edit-audience-pains" chipTone="amber"
                    placeholder={t("products:form.audiencePainsPlaceholder")} />
                </Field>
                <Field label={t("products:form.audienceDesiresLabel")} hint={t("products:form.audienceDesiresHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.audience_desires || []}
                    onChange={(v) => setProduct({ ...product, audience_desires: v })}
                    testId="product-edit-audience-desires"
                    placeholder={t("products:form.audienceDesiresPlaceholder")} />
                </Field>
                <Field label={t("products:form.audiencePreviousAttemptsLabel")} hint={t("products:form.audiencePreviousAttemptsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.audience_previous_attempts || []}
                    onChange={(v) => setProduct({ ...product, audience_previous_attempts: v })}
                    testId="product-edit-audience-previous-attempts" chipTone="slate"
                    placeholder={t("products:form.audiencePreviousAttemptsPlaceholder")} />
                </Field>
                <Field label={t("products:form.audienceDeepDesiresLabel")} hint={t("products:form.audienceDeepDesiresHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.audience_deep_desires || []}
                    onChange={(v) => setProduct({ ...product, audience_deep_desires: v })}
                    testId="product-edit-audience-deep-desires"
                    placeholder={t("products:form.audienceDeepDesiresPlaceholder")} />
                </Field>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              testId="product-section-expressions"
              title={t("products:sections.expressions.title")}
              subtitle={t("products:sections.expressions.subtitle")}
              aiFill={productAiFill("audience_deep_dive", t("products:sections.expressions.title"))}
            >
              <Field label={t("products:form.audienceExpressionsLabel")} hint={t("products:form.audienceExpressionsHint")}>
                <ChipListEditor disabled={!canWrite} value={product.audience_expressions || []}
                  onChange={(v) => setProduct({ ...product, audience_expressions: v })}
                  testId="product-edit-audience-expressions"
                  placeholder={t("products:form.audienceExpressionsPlaceholder")} />
              </Field>
            </CollapsibleSection>

            <CollapsibleSection
              testId="product-section-demographics"
              title={t("products:sections.demographics.title")}
              subtitle={t("products:sections.demographics.subtitle")}
              defaultOpen={false}
              aiFill={productAiFill("audience", t("products:sections.demographics.title"))}
            >
              <DemographicsEditor
                disabled={!canWrite}
                value={product.audience_demographics || {}}
                onChange={(v) => setProduct({ ...product, audience_demographics: v })}
              />
              <div className="mt-4">
                <Field label={t("products:form.audiencePsychographicsLabel")} hint={t("products:form.audiencePsychographicsHint")}>
                  <ChipListEditor disabled={!canWrite} value={product.audience_psychographics || []}
                    onChange={(v) => setProduct({ ...product, audience_psychographics: v })}
                    testId="product-edit-audience-psychographics"
                    placeholder={t("products:form.audiencePsychographicsPlaceholder")} />
                </Field>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              testId="product-section-firmographics"
              title={t("products:sections.firmographics.title")}
              subtitle={t("products:sections.firmographics.subtitle")}
              defaultOpen={false}
              aiFill={productAiFill("audience", t("products:sections.firmographics.title"))}
            >
              <FirmographicsEditor
                disabled={!canWrite}
                value={product.audience_firmographics || {}}
                onChange={(v) => setProduct({ ...product, audience_firmographics: v })}
              />
            </CollapsibleSection>

            {canWrite && <FormFooter saving={saving} onDelete={() => setConfirmDelete(true)} onArchive={onArchive} archiveBusy={archiveBusy} isArchived={isArchived} t={t} />}
          </form>
        </TabsContent>

        {/* ─── COMPETITORS ─── */}
        <TabsContent value="competitors">
          <div className="space-y-4">
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setOpenCompetitorCreate(true)}
                  data-testid="product-new-competitor-manual"
                  className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:competitors.addManual")}
                </Button>
                <Button
                  onClick={() => setOpenCompetitorSuggest(true)}
                  data-testid="product-suggest-competitors"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:competitors.suggestAi")}
                </Button>
              </div>
            )}
            {competitors.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid="product-no-competitors">
                {t("products:detail.noCompetitors")}
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <ProductCompetitorTable
                  competitors={competitors}
                  canWrite={canWrite}
                  onDelete={setPendingCompetitorDelete}
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* ─── SOURCES ─── */}
        <TabsContent value="sources">
          {product.autofill_source?.raw_pages?.length ? (
            <div className="space-y-2">
              {product.autofill_source.raw_pages.map((p, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <a href={p.url} target="_blank" rel="noreferrer" className="block truncate text-sm font-medium text-indigo-700 hover:text-indigo-900">
                      {p.title || p.url}
                    </a>
                    {p.excerpt && <p className="mt-1 text-xs text-slate-500">{p.excerpt}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
              {t("products:detail.noSources")}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ─── Modals ─── */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="product-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("products:edit.delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("products:edit.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-confirm-delete-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} disabled={deleting} data-testid="product-confirm-delete-confirm" className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("products:edit.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateProductCompetitorModal
        open={openCompetitorCreate}
        onOpenChange={setOpenCompetitorCreate}
        product={product}
        brand={brand}
        onCreated={(c) => setCompetitors([c, ...competitors])}
      />

      <SuggestProductCompetitorsModal
        open={openCompetitorSuggest}
        onOpenChange={setOpenCompetitorSuggest}
        product={product}
        brand={brand}
        onAdded={(added) => setCompetitors([...added, ...competitors])}
      />

      <AlertDialog open={!!pendingCompetitorDelete} onOpenChange={(o) => { if (!o) setPendingCompetitorDelete(null); }}>
        <AlertDialogContent data-testid="product-competitor-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("competitors:edit.delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCompetitorDelete ? t("competitors:deleteConfirm", { name: pendingCompetitorDelete.name }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-competitor-confirm-delete-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCompetitor}
              disabled={competitorDeleting}
              data-testid="product-competitor-confirm-delete-confirm"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {competitorDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("competitors:edit.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Helpers ───
function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
        {required ? <span className="ml-0.5 text-rose-500">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}

function FormFooter({ saving, onDelete, onArchive, archiveBusy, isArchived, t }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-100 bg-white p-4">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={onDelete} data-testid="product-delete-button" className="border-rose-200 text-rose-700 hover:bg-rose-50">
          <Trash2 className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("products:edit.delete")}
        </Button>
        {!isArchived && (
          <Button
            type="button" variant="outline"
            onClick={onArchive} disabled={archiveBusy}
            data-testid="product-archive-button"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {archiveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("products:detail.archiveAction")}</>}
          </Button>
        )}
      </div>
      <Button type="submit" disabled={saving} data-testid="product-save-button" className="bg-slate-900 text-white hover:bg-slate-800">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("common:save")}</>}
      </Button>
    </div>
  );
}

function DemographicsEditor({ value, onChange, disabled }) {
  const { t } = useTranslation("products");
  const set = (k, v) => onChange({ ...(value || {}), [k]: v });
  const v = value || {};
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {["age_range", "gender", "location", "income", "education", "occupation"].map((k) => (
        <div key={k} className="space-y-1.5">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {t(`form.demographics.${k}`)}
          </Label>
          <Input
            disabled={disabled}
            value={v[k] || ""}
            onChange={(e) => set(k, e.target.value)}
            data-testid={`product-edit-demographics-${k}`}
            className="h-11"
          />
        </div>
      ))}
    </div>
  );
}

function FirmographicsEditor({ value, onChange, disabled }) {
  const { t } = useTranslation("products");
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("form.firmographics.company_size")}</Label>
          <Input disabled={disabled} value={v.company_size || ""}
                 onChange={(e) => set("company_size", e.target.value)}
                 data-testid="product-edit-firmographics-company-size" className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("form.firmographics.industry")}</Label>
          <Input disabled={disabled} value={v.industry || ""}
                 onChange={(e) => set("industry", e.target.value)}
                 data-testid="product-edit-firmographics-industry" className="h-11" />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("form.firmographics.geo")}</Label>
          <Input disabled={disabled} value={v.geo || ""}
                 onChange={(e) => set("geo", e.target.value)}
                 data-testid="product-edit-firmographics-geo" className="h-11" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("form.firmographics.role_titles")}</Label>
        <ChipListEditor disabled={disabled} value={v.role_titles || []}
          onChange={(arr) => set("role_titles", arr)}
          testId="product-edit-firmographics-role-titles"
          placeholder={t("form.firmographics.role_titlesPlaceholder")} />
      </div>
    </div>
  );
}

// ─── Competitor table for the product Competitors tab ───
function ProductCompetitorTable({ competitors, canWrite, onDelete }) {
  const { t } = useTranslation("competitors");
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50">
        <tr className="border-b border-slate-200">
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.name")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.scope")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.relation")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.confidence")}</th>
          <th className="px-6 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500" aria-label="actions"></th>
        </tr>
      </thead>
      <tbody>
        {competitors.map((c) => (
          <tr key={c.id} data-testid={`product-competitor-row-${c.id}`} className="border-t border-slate-100">
            <td className="px-6 py-4">
              <div className="flex flex-col">
                <span className="font-medium text-slate-900">{c.name}</span>
                {c.url && (
                  <a href={c.url} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-indigo-600">
                    {c.url.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </td>
            <td className="px-6 py-4">
              <Badge
                variant="outline"
                data-testid={`product-competitor-scope-${c.id}`}
                className={c.scope === "product"
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-slate-50 text-slate-700"}
              >
                {c.scope === "product" ? t("scope.product") : t("scope.brand")}
              </Badge>
            </td>
            <td className="px-6 py-4 text-slate-600">{t(`relation.${c.relation_type}`)}</td>
            <td className="px-6 py-4 text-slate-600">{Math.round((c.confidence || 0) * 100)}%</td>
            <td className="px-6 py-4 text-right">
              {canWrite && c.scope === "product" ? (
                <Button
                  type="button" size="sm" variant="ghost"
                  data-testid={`product-competitor-delete-${c.id}`}
                  onClick={() => onDelete(c)}
                  className="h-8 px-2 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CreateProductCompetitorModal({ open, onOpenChange, product, brand, onCreated }) {
  const { t } = useTranslation(["competitors", "common", "errors"]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [relation, setRelation] = useState("direct");
  const [confidence, setConfidence] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!open) { setName(""); setUrl(""); setRelation("direct"); setConfidence(1); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${product.id}/competitors`, {
        name: name.trim(),
        url: url.trim() || null,
        relation_type: relation,
        confidence: Number(confidence),
      });
      toast.success(t("competitors:edit.saveSuccess"));
      onCreated({ ...data, scope: "product" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-product-competitor-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">
            {t("competitors:create.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {t("competitors:productCreate.subtitle", { product: product?.name, brand: brand?.name })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.nameLabel")}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("competitors:form.namePlaceholder")} data-testid="create-product-competitor-name" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.urlLabel")}</Label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("competitors:form.urlPlaceholder")} data-testid="create-product-competitor-url" className="h-11" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.relationLabel")}</Label>
              <Select value={relation} onValueChange={setRelation}>
                <SelectTrigger data-testid="create-product-competitor-relation" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATION_TYPES.map((r) => (
                    <SelectItem key={r} value={r}>{t(`competitors:relation.${r}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.confidenceLabel")}</Label>
              <Input type="number" min="0" max="1" step="0.1" value={confidence} onChange={(e) => setConfidence(e.target.value)} data-testid="create-product-competitor-confidence" className="h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="create-product-competitor-cancel">{t("common:cancel")}</Button>
            <Button type="submit" disabled={submitting} data-testid="create-product-competitor-submit" className="bg-slate-900 text-white hover:bg-slate-800">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("competitors:create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuggestProductCompetitorsModal({ open, onOpenChange, product, brand, onAdded }) {
  const { t } = useTranslation(["brands", "competitors", "common", "errors", "autofill"]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [errorKey, setErrorKey] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open || !brand) { setSuggestions([]); setPicked(new Set()); setErrorKey(null); return; }
    setLoading(true);
    setErrorKey(null);
    api.post("/autofill/competitors", { brand_id: brand.id, product_id: product?.id, max: 8 })
      .then(({ data }) => {
        setSuggestions(data?.competitors || []);
        setPicked(new Set((data?.competitors || []).map((_, i) => i)));
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "";
        if (typeof detail === "string" && detail) setErrorKey(detail);
        else setErrorKey("generic");
      })
      .finally(() => setLoading(false));
  }, [open, brand, product]);

  const togglePick = (i) => {
    const next = new Set(picked);
    if (next.has(i)) next.delete(i); else next.add(i);
    setPicked(next);
  };

  const save = async () => {
    if (!product) return;
    setAdding(true);
    try {
      const toCreate = suggestions.filter((_, i) => picked.has(i));
      const { data } = await api.post(`/products/${product.id}/competitors/from_autofill`, {
        draft: { competitors: toCreate },
      });
      const added = (data || []).map((d) => ({ ...d, id: d._id || d.id, scope: "product", discovered_by: "ai_suggestion" }));
      toast.success(t("brands:competitors.suggestions.added", { count: added.length }));
      onAdded(added);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="suggest-product-competitors-modal" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight">
            <Sparkles className="h-4 w-4 text-indigo-600" strokeWidth={2} />
            {t("brands:competitors.suggestions.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("brands:competitors.suggestions.subtitle")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common:loading")}</div>
        ) : errorKey === "llm_failed" ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <p className="font-display text-sm font-semibold text-rose-900">{t("errors:llm_failed.title")}</p>
            <p className="mt-1 text-xs text-rose-800/80">{t("errors:llm_failed.body")}</p>
          </div>
        ) : suggestions.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            {t("brands:competitors.suggestions.noResults")}
          </p>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {suggestions.map((s, i) => (
              <label
                key={i}
                data-testid={`suggested-product-competitor-${i}`}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${picked.has(i) ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <input type="checkbox" checked={picked.has(i)} onChange={() => togglePick(i)} className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-display text-sm font-semibold text-slate-900">{s.name}</span>
                    <span className="text-[11px] text-slate-500">{t(`competitors:relation.${s.relation_type || "direct"}`)}</span>
                    <span className="text-[11px] text-slate-400">{t("brands:competitors.suggestions.confidence")}: {Math.round((s.confidence || 0) * 100)}%</span>
                  </div>
                  {s.url && <p className="mt-0.5 truncate text-[11px] text-slate-500">{s.url.replace(/^https?:\/\//, "")}</p>}
                  {s.reason && <p className="mt-1 text-xs text-slate-500">{s.reason}</p>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="suggest-product-competitors-cancel">{t("common:cancel")}</Button>
          {!errorKey && suggestions.length > 0 && (
            <Button type="button" onClick={save} disabled={adding || picked.size === 0} data-testid="suggest-product-competitors-save" className="bg-slate-900 text-white hover:bg-slate-800">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" strokeWidth={2} />{t("brands:competitors.suggestions.include")} ({picked.size})</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
